import {Component, ElementRef, OnInit, ViewChild, inject, output, input, effect} from '@angular/core';
import {FileType, ViewMode} from "../../../interfaces";
import Swal from "sweetalert2";
import {DataService} from "../../../../services/data.service";
import {FormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {AccessService} from "../../../../services/access.service";
import {FormService} from "../../../../services/form.service";
import {ToastrService} from "ngx-toastr";
import {NgIf, NgFor} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {FiletypePipe} from '../../../../pipes/filetype.pipe';

@Component({
    selector: 'app-research-documents',
    templateUrl: './research-documents.component.html',
    styleUrl: './research-documents.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, FiletypePipe]
})
export class ResearchDocumentsComponent implements OnInit {

    accessService = inject(AccessService);
    formService = inject(FormService);
    private dataService = inject(DataService);
    private fb = inject(FormBuilder);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    @ViewChild('fileInput') fileInput: ElementRef;

    readonly ViewMode = ViewMode;

    viewMode!: ViewMode;
    project: any;
    files: any[] = [];
    links: any[] = [];
    form!: UntypedFormGroup;

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getFiles();
            this.getLinks();
        }
    })

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            links: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getFiles();
            this.getLinks();
        }
    }

    triggerFileInput() {
        this.fileInput.nativeElement.click();
    }

    getFiles(): void {
        this.dataService.request('File/listAll', {
            PID: null,
            RID: this.PID(),
            category: FileType.DOCUMENT,
        }).subscribe(response => {
            this.files = response.files;
        });
    }

    getLinks(): void {
        this.dataService.request('Link/listAll', {
            PID: null,
            RID: this.PID()
        }).subscribe(response => {
            this.links = response.links;
            this.patchForm();
        });
    }

    patchForm(): void {
        this.formService.getFormArray(this.form, 'links').clear();
        this.links.forEach(item => {
            this.addFormItem(item, 'links');
        });
    }

    addFormItem(data: any = {}, control): void {
        this.formService.getFormArray(this.form, control).push(
            this.fb.group({
                id: [data.id || null],
                id_project: [data.id_project || this.PID()],
                name: [data.name || ''],
                url: [data.url || ''],
                _deleted: [false],
            })
        );
    }

    handleInputChange(e): void {
        for (let i = 0; i < e.target.files.length; i++) {
            const reader = new FileReader();
            reader.onload = (progressEvent) => {
                this.files.push({
                    name: e.target.files[i].name,
                    size: e.target.files[i].size,
                    type: e.target.files[i].type,
                    data: progressEvent.target.result,
                    id: null,
                    category: FileType.DOCUMENT,
                });
                if (i == e.target.files.length - 1) {
                    this.fileInput.nativeElement.value = '';
                }
            };
            reader.readAsDataURL(e.target.files[i]);
        }
    }

    deleteFile(file: any): void {
        Swal.fire({
            title: 'Sicher?',
            text: 'Der Anhang wird gelöscht.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Ja',
            cancelButtonText: 'Nein'
        }).then(result => {
            if (result.value) {
                this.dataService.request('File/delete', {
                    id: file.id
                }).subscribe(() => {
                    this.getFiles();
                });
            }
        });
    }

    getFileData(file: any): void {
        if (file.id == null) {
            this.openFile(file.data, file.name);
        } else {
            this.dataService.request("File/getData", {
                id: file.id
            }).subscribe(response => {
                if (response.data.data != null) {
                    this.openFile('data:' + response.data.type + ';base64,' + response.data.data, response.data.name);
                }
            });
        }
    }

    deleteOrRemoveFile(file: any) {
        if (file.id == null) {
            this.files.splice(this.files.indexOf(file), 1);
        } else {
            this.deleteFile(file);
        }
    }

    openFile(data: string, name: string): void {
        const link = document.createElement('a');

        link.setAttribute('href', data);
        link.setAttribute('download', name);
        link.click();
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        this.dataService.request('File/save', {
            PID: null,
            RID: this.PID(),
            files: this.files,
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.setMode(ViewMode.VIEW);
            this.getFiles();
            this.changeProject.emit();
        });

        this.dataService.request('Link/save', {
            PID: null,
            RID: this.PID(),
            project: this.form.getRawValue(),
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.getLinks();
        });
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    markEntryAsDeleted(entry): void {
        entry.patchValue({
            _deleted: true,
        });
        for (const key in entry.controls) {
            entry.get(key).clearValidators();
            entry.get(key).updateValueAndValidity();
        }
    }

    getLengthOfFormArray(name): number {
        return this.formService.getFormArray(this.form, name).controls.filter(control => {
            return control.value._deleted == false;
        }).length;
    }
}
